import React from 'react';
import { useTheme } from '../../../assets/contexts/ThemeContext';

const DEFAULT_PRIMARY = '#e97730';
const DEFAULT_SECONDARY = '#146e7a';

/**
 * 테마 색상 제어 컴포넌트
 * 원본: color-control.html
 */
const ColorControl = () => {
  const { themeConfig, set } = useTheme();
  const currentThemeConfig = themeConfig || {};
  const [colorText, setColorText] = React.useState(currentThemeConfig.themeColor || DEFAULT_PRIMARY);
  const [secondaryColorText, setSecondaryColorText] = React.useState(
    currentThemeConfig.themeColorSecondary || DEFAULT_SECONDARY
  );
  const [isValidColor, setIsValidColor] = React.useState(true);
  const [isValidSecondaryColor, setIsValidSecondaryColor] = React.useState(true);

  const isValidHexColor = (color) => /^#[0-9A-F]{6}$/i.test(color);

  const updateThemeColor = React.useCallback(
    (colorValue) => {
      if (!isValidHexColor(colorValue)) return;

      set('themeColor', colorValue);
      document.documentElement.style.setProperty('--theme-color-primary', colorValue);

      // 헤더와 테두리 업데이트
      const header = document.getElementById('theme-control-header');
      const wrapper = document.getElementById('theme-control-wrapper');
      if (header) header.style.background = colorValue;
      if (wrapper) wrapper.style.borderColor = colorValue;
    },
    [set]
  );

  const updateThemeSecondaryColor = React.useCallback(
    (colorValue) => {
      if (!isValidHexColor(colorValue)) return;

      set('themeColorSecondary', colorValue);
      document.documentElement.style.setProperty('--theme-color-secondary', colorValue);
    },
    [set]
  );

  React.useEffect(() => {
    const color = currentThemeConfig.themeColor || DEFAULT_PRIMARY;
    setColorText(color);
    updateThemeColor(color);
  }, [currentThemeConfig.themeColor, updateThemeColor]);

  React.useEffect(() => {
    const color = currentThemeConfig.themeColorSecondary || DEFAULT_SECONDARY;
    setSecondaryColorText(color);
    updateThemeSecondaryColor(color);
  }, [currentThemeConfig.themeColorSecondary, updateThemeSecondaryColor]);

  return (
    <div id="color-control-section">
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
        <label style={{ fontWeight: 'bold', color: '#333', fontSize: '13px', margin: 0, flex: 1 }}>🎨 테마 색상:</label>
        <button
          type="button"
          className="theme-help-btn"
          data-target="color-control-section"
          style={{
            background: 'transparent',
            border: '1px solid #ddd',
            color: '#666',
            fontSize: '11px',
            width: '20px',
            height: '20px',
            padding: 0,
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="이 항목으로 이동"
        >
          ?
        </button>
      </div>
      <div style={{ fontSize: '11px', color: '#999', marginBottom: '10px', lineHeight: 1.4 }}>
        주 색상(강조)과 보조 색상(티일·보조 UI)을 각각 설정할 수 있습니다.
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: 500 }}>주 테마 색상:</label>
          <button
            type="button"
            onClick={() => {
              setColorText(DEFAULT_PRIMARY);
              updateThemeColor(DEFAULT_PRIMARY);
            }}
            style={{
              background: 'transparent',
              border: '1px solid #ddd',
              color: '#666',
              fontSize: '10px',
              padding: '2px 8px',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
          >
            리셋
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <input
            type="color"
            id="theme-color"
            value={currentThemeConfig.themeColor || DEFAULT_PRIMARY}
            onChange={(e) => {
              const color = e.target.value;
              setColorText(color);
              updateThemeColor(color);
            }}
            style={{
              width: '50px',
              height: '35px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              padding: 0
            }}
            title="주 테마 색상 선택"
          />
          <div style={{ flex: 1 }}>
            <input
              type="text"
              id="theme-color-text"
              value={colorText}
              onChange={(e) => {
                const color = e.target.value.trim();
                setColorText(color);
                const isValid = isValidHexColor(color);
                setIsValidColor(isValid);
                if (isValid) {
                  updateThemeColor(color);
                }
              }}
              onBlur={(e) => {
                const color = e.target.value.trim();
                if (!isValidHexColor(color)) {
                  const currentColor = currentThemeConfig.themeColor || DEFAULT_PRIMARY;
                  setColorText(currentColor);
                  setIsValidColor(true);
                }
              }}
              placeholder={DEFAULT_PRIMARY}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: `1px solid ${isValidColor ? '#ddd' : '#ff4444'}`,
                borderRadius: '4px',
                fontSize: '12px',
                fontFamily: 'monospace'
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: 500 }}>보조 테마 색상:</label>
          <button
            type="button"
            onClick={() => {
              setSecondaryColorText(DEFAULT_SECONDARY);
              updateThemeSecondaryColor(DEFAULT_SECONDARY);
            }}
            style={{
              background: 'transparent',
              border: '1px solid #ddd',
              color: '#666',
              fontSize: '10px',
              padding: '2px 8px',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
          >
            리셋
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input
            type="color"
            id="theme-color-secondary"
            value={currentThemeConfig.themeColorSecondary || DEFAULT_SECONDARY}
            onChange={(e) => {
              const color = e.target.value;
              setSecondaryColorText(color);
              updateThemeSecondaryColor(color);
            }}
            style={{
              width: '50px',
              height: '35px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              padding: 0
            }}
            title="보조 테마 색상 선택"
          />
          <div style={{ flex: 1 }}>
            <input
              type="text"
              id="theme-color-secondary-text"
              value={secondaryColorText}
              onChange={(e) => {
                const color = e.target.value.trim();
                setSecondaryColorText(color);
                const isValid = isValidHexColor(color);
                setIsValidSecondaryColor(isValid);
                if (isValid) {
                  updateThemeSecondaryColor(color);
                }
              }}
              onBlur={(e) => {
                const color = e.target.value.trim();
                if (!isValidHexColor(color)) {
                  const currentColor = currentThemeConfig.themeColorSecondary || DEFAULT_SECONDARY;
                  setSecondaryColorText(currentColor);
                  setIsValidSecondaryColor(true);
                }
              }}
              placeholder={DEFAULT_SECONDARY}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: `1px solid ${isValidSecondaryColor ? '#ddd' : '#ff4444'}`,
                borderRadius: '4px',
                fontSize: '12px',
                fontFamily: 'monospace'
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorControl;
